/**
 * Who this device is, and the short code it stamps on provisional numbers.
 *
 * Both are minted once and kept. The id identifies the device to `/v1/sync/push`
 * and is the tiebreak that makes field-level merge converge identically on
 * every replica (D3) — so it must survive a reload, or two tabs on the same
 * phone become two devices and start breaking ties against each other.
 *
 * The short code is what a customer actually reads. `J-D4-0007` is visibly
 * provisional to a human and unique without coordination (D10), and the whole
 * point is that it does not change between the job being created in a comms
 * room and the paperwork being handed over at the door.
 */

import { uuid } from "./uuid";

const DEVICE_KEY = "keystone.device";

export type Device = {
  id: string;
  /** Two characters, uppercase. Short enough to read out over the phone. */
  shortCode: string;
};

function mint(): Device {
  const id = uuid();
  // Taken from the id rather than generated separately, so the short code can
  // always be traced back to the device that minted it. Two hex characters is
  // 256 possibilities — a collision between two of a business's own vans is
  // possible but not harmful, because the number is provisional and the server
  // replaces it. A longer code would be worse at the thing it is for, which is
  // being read out loud.
  return { id, shortCode: id.replace(/-/g, "").slice(0, 2).toUpperCase() };
}

function isDevice(value: unknown): value is Device {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Device).id === "string" &&
    typeof (value as Device).shortCode === "string"
  );
}

let cached: Device | null = null;

/**
 * This device's identity, minted on first call.
 *
 * Falls back to an in-memory identity when storage is unavailable — a private
 * window, or blocked site data. That device loses its identity on reload,
 * which costs it its queued work's provenance but never breaks the app; the
 * alternative is refusing to run at all.
 */
export function device(): Device {
  if (cached) return cached;

  try {
    const stored = localStorage.getItem(DEVICE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (isDevice(parsed)) {
        cached = parsed;
        return cached;
      }
    }
  } catch {
    /* fall through to minting */
  }

  cached = mint();
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(cached));
  } catch {
    /* in-memory for this session */
  }
  return cached;
}

/**
 * A new identity for this install (D93): what a phone needs when it moves to
 * another business. The server keeps a device in the business it first
 * signed in to — whatever it carried belongs there — so the phone takes a new
 * id rather than dragging the old one across.
 */
export function newDevice(): Device {
  cached = mint();
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(cached));
  } catch {
    /* in-memory for this session */
  }
  return cached;
}

/** The server's refusal of a device that belongs to another business. */
export function isRegisteredElsewhere(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { status, message } = error as { status?: unknown; message?: unknown };
  return status === 409 && typeof message === "string" && /registered to another organisation/i.test(message);
}

/** Test seam. Not called by the app. */
export function resetDeviceForTests() {
  cached = null;
  try {
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * A provisional document number, matching the server's `PROVISIONAL` regex in
 * `app/services/numbering.py`. The two have to agree: the server refuses a
 * `client_ref` that is not provisional, precisely so a device cannot smuggle
 * in what looks like a real number.
 */
export function provisionalNumber(prefix: string, localSeq: number): string {
  return `${prefix}-${device().shortCode}-${String(localSeq).padStart(4, "0")}`;
}
